/**
 * The **running morph** as module state — shaped 1:1 like `chatHandoff.ts`:
 * id-based, with an expiry instead of a cleaner, idempotent under
 * `<React.StrictMode>`, read via `useSyncExternalStore`.
 *
 * Deliberately **not a jotai atom** (same reasoning as there): `index.tsx`
 * mounts `<Provider>` without its own store, and registration happens from
 * inside `useGuideNavigate` — i.e. from the message renderer, deep below the
 * shell.
 *
 * **Frozen decision.** Whether to morph at all is stored as a value in the
 * record (`decision`), not asked of the environment. The navigator reads
 * `prefers-reduced-motion` and the window width **once** on click; after that
 * the overlay reads only this field. Toggling the system setting or dragging
 * the window mid-flight must not switch the branch — the cancel path (target
 * rect not measurable, `resize`) remains as a coarse safety net underneath.
 *
 * **Expiry.** A registered morph that nobody picks up (the target lies outside
 * the shell, `/markup`, `/logout` — no overlay mounts there) must not flash up
 * somewhere later. It is valid for `MORPH_TTL_MS`; after that it is moot
 * without anyone having to clean up.
 */

import { useSyncExternalStore } from 'react';

import { foldAddress } from '../address';
import type { MorphRect } from './assistantSurface';
import { MORPH_CANCEL_FADE_MS, MORPH_MAX_MS } from './morphTimeline';

/**
 * Window within which a registered morph must be picked up: the **same**
 * number as the watchdog `MORPH_MAX_MS`, not a second one beside it — a morph
 * that has found no one after 1.2 s will not find anyone anymore. Both now
 * drift together by construction.
 */
export const MORPH_TTL_MS = MORPH_MAX_MS;

/** What the navigator knew about the environment at click time — frozen. */
export interface MorphDecision {
    reducedMotion: boolean;
    isMobile: boolean;
    /**
     * The outcome: flight or not. **The** question `AppShell` asks
     * (`morphHere`) — not `useIsMobile()`, not `useReducedMotion()`.
     */
    morph: boolean;
}

export interface AssistantMorph {
    id: string;
    /** The handoff this flight belongs to (`chatHandoff.ts`). */
    handoffId: string;
    /** Jump target as `pathname + search` — binding, as with the handoff. */
    target: string;
    /** Start rect of the shell (the visible conversation column). */
    originRect: MorphRect;
    /** Rect of the whole chat surface — gives the clone's position inside the shell. */
    surfaceRect: MorphRect;
    scrollTop: number;
    /** Resolved fill colour, see `measureAssistantSurface`. */
    fill: string;
    /** Layer A: pixel-exact clone of the chat surface. */
    ghost: HTMLElement;
    /** Layer B: the conversation from which the panel preview is built. */
    snapshot: unknown;
    decision: MorphDecision;
    startedAt: number;
    /**
     * `flying` = the shell is flying, the real panel is invisible;
     * `handover` = both are visible, 120 ms crossfade;
     * `cancelled` = the flight failed (window changed, tab hidden, target rect
     * not measurable) — the shell fades out over `MORPH_CANCEL_FADE_MS` while
     * the real panel is already fading in.
     * If cancelling went straight to `null`, the shell would vanish in *one*
     * commit while the panel still sat at `opacity: 0` — exactly the white
     * frame this design rules out.
     * A finished morph is **not** a state but `null` — so the panel can never
     * get stuck invisible if someone forgets to clean up.
     */
    phase: 'flying' | 'handover' | 'cancelled';
    /** When the real panel had its history (ms since `startedAt`), otherwise `null`. */
    panelReadyMs: number | null;
}

export interface AssistantMorphRequest {
    handoffId: string;
    target: string;
    originRect: MorphRect;
    surfaceRect: MorphRect;
    scrollTop: number;
    fill: string;
    ghost: HTMLElement;
    snapshot: unknown;
    decision: MorphDecision;
}

let current: AssistantMorph | null = null;
let sequence = 0;
const listeners = new Set<() => void>();
/** Waiters for "no morph anymore" — see `whenMorphIdle`. */
let idleWaiters: Array<() => void> = [];
/**
 * The cleaner of the module state. `isMorphFresh` only *hides* an expired
 * flight — without this timer its DOM clone (a detached transcript subtree)
 * would stay attached to the module until the next flight. That happens on
 * every jump out of the chat to a target without shell (`/markup`, `/logout`),
 * where no overlay and no cleanup effect runs.
 */
let expiryTimer: ReturnType<typeof setTimeout> | null = null;

function clearExpiry(): void {
    if (expiryTimer === null) return;
    clearTimeout(expiryTimer);
    expiryTimer = null;
}

function armExpiry(morph: AssistantMorph, inMs: number): void {
    clearExpiry();
    const { id } = morph;
    expiryTimer = setTimeout(() => {
        expiryTimer = null;
        if (current && current.id === id) publish(null);
    }, Math.max(0, inMs));
}

function publish(next: AssistantMorph | null): void {
    if (current === next) return;
    current = next;
    if (next === null) {
        clearExpiry();
    } else {
        armExpiry(
            next,
            next.phase === 'cancelled'
                ? MORPH_CANCEL_FADE_MS
                : MORPH_TTL_MS - (Date.now() - next.startedAt),
        );
    }
    listeners.forEach((listener) => listener());
    if (next === null && idleWaiters.length > 0) {
        const waiting = idleWaiters;
        idleWaiters = [];
        waiting.forEach((resolve) => resolve());
    }
}

/** Registers a flight — in the same click handler as `requestChatHandoff`. */
export function beginAssistantMorph(
    request: AssistantMorphRequest,
    now: number = Date.now(),
): AssistantMorph {
    const morph: AssistantMorph = {
        ...request,
        id: `morph-${now.toString(36)}-${(sequence += 1)}`,
        startedAt: now,
        phase: 'flying',
        panelReadyMs: null,
    };
    publish(morph);
    return morph;
}

export function isMorphFresh(morph: AssistantMorph | null, now: number = Date.now()): boolean {
    if (!morph) return false;
    const age = now - morph.startedAt;
    return age >= 0 && age < MORPH_TTL_MS;
}

export function peekAssistantMorph(now: number = Date.now()): AssistantMorph | null {
    return isMorphFresh(current, now) ? current : null;
}

/**
 * Does this flight apply *here*? Compared folded (`foldAddress`) — the same
 * target binding as for the handoff and the self-navigation marker. It also
 * covers the reverse direction: a jump from a panel page to `/chatbot` must
 * not suck the panel back in.
 */
export function isMorphFor(
    morph: AssistantMorph | null,
    address: string,
    now: number = Date.now(),
): boolean {
    if (!isMorphFresh(morph, now)) return false;
    return foldAddress(morph!.target) === foldAddress(address);
}

/**
 * Foreign navigation, back button, window size, hidden tab — the flight ends.
 *
 * **Not** as a hard cut: the record moves to phase `cancelled`, the shell
 * fades out over `MORPH_CANCEL_FADE_MS` and the real panel fades in during the
 * same milliseconds (`morphing` only applies to `flying`). Only after that
 * does the state drop to `null` — and it does so here in the module, not in
 * the overlay: a cancel also covers the case where no overlay is mounted at
 * all.
 */
export function cancelAssistantMorph(): void {
    if (!current || current.phase === 'cancelled') return;
    publish({ ...current, phase: 'cancelled' });
}

/**
 * The real panel has its history (or has failed for good). This is the signal
 * the handoff waits for: without it the panel might fade in with bare help
 * text while the preview showed the history.
 */
export function markPanelReady(handoffId: string, now: number = Date.now()): void {
    if (!current || current.handoffId !== handoffId) return;
    // A cancelled flight waits for nothing anymore — and must not reset its
    // expiry through the update either.
    if (current.phase === 'cancelled') return;
    if (current.panelReadyMs !== null) return;
    publish({ ...current, panelReadyMs: Math.max(0, now - current.startedAt) });
}

/** The flight is done — now the shell and the real panel crossfade. */
export function beginMorphHandover(id: string): void {
    if (!current || current.id !== id || current.phase !== 'flying') return;
    publish({ ...current, phase: 'handover' });
}

/** End. The state **always** returns here — via the watchdog too. */
export function markMorphDone(id: string): void {
    if (!current || current.id !== id) return;
    publish(null);
}

/**
 * Resolves as soon as no flight is running — immediately if none is running.
 * `OverlayBotChat` hangs the grey „Ich bin mitgekommen" line on it: under the
 * shell it would appear unnoticed, and its `.oa-enter` animation would be
 * wasted.
 *
 * The promise is **unconditional**: it resolves at the latest when the flight
 * expires (`MORPH_TTL_MS`). A morph whose overlay never mounts — the target
 * lay outside the shell — has no one to end it; without this deadline the grey
 * line would never appear.
 */
export function whenMorphIdle(now: number = Date.now()): Promise<void> {
    if (!isMorphFresh(current, now)) return Promise.resolve();
    const remaining = Math.max(0, MORPH_TTL_MS - (now - current!.startedAt));
    return new Promise((resolve) => {
        let settled = false;
        const settle = () => {
            if (settled) return;
            settled = true;
            idleWaiters = idleWaiters.filter((waiter) => waiter !== settle);
            resolve();
        };
        idleWaiters.push(settle);
        setTimeout(settle, remaining);
    });
}

/**
 * Tests only: the **raw** module state, without freshness check. This lets us
 * verify that an expired flight is really cleared and not merely hidden by
 * `peekAssistantMorph` — the difference is a retained DOM clone.
 */
export function rawAssistantMorphForTest(): AssistantMorph | null {
    return current;
}

/** Tests only. */
export function resetAssistantMorph(): void {
    publish(null);
    idleWaiters = [];
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/**
 * Deliberately the **raw** module state, not the freshness check — a
 * `getSnapshot` that reads the clock would return different values within one
 * render and drive React into an infinite loop. The callers decide about
 * expiry (`isMorphFor`).
 */
function snapshot(): AssistantMorph | null {
    return current;
}

export function useAssistantMorph(): AssistantMorph | null {
    return useSyncExternalStore(subscribe, snapshot, snapshot);
}
